import { useCallback, useMemo, useState } from "react";
import {
	addDurationMetaDataEvents,
	areSourcesAvailable,
	convertLoggingEventsToVideoEvents,
	filterVideoEvents,
	findStartDate,
	getLoggedEvents,
	getVideoUrls,
	getRoomScanVideoUrl,
} from "./proctoringResultViewHelper";
import { ProctoringResultViewProps } from "../ProctoringResultView";
import useTimestamps, { VideoEvent } from "../../../../utilities/useTimestamps";
import { ProctoringAssessmentConfigurationV1 } from "@verity/proctoring-option/types";
import { RecordingSourceVideoUrls, SessionNote } from "../ProctoringVideoPlayer";
import { isValidEvent } from "../Timestamps/TimestampLinks";

export type LiveProctoringSessionNote = SessionNote & {
	id: string;
};

type useVideoEventsProps = Pick<ProctoringResultViewProps, "courseId" | "selectedLiveNotes">;

export default function useVideoEvents({ courseId, selectedLiveNotes }: useVideoEventsProps) {
	const [areVideosFetched, setAreVideosFetched] = useState(false);

	const [startDate, setStartDate] = useState<Date>(new Date(0));
	const [videoEvents, setVideoEvents] = useState<VideoEvent[]>([]);

	const [screenVidSources, setScreenVidSources] = useState<RecordingSourceVideoUrls>({});
	const [webcamVidSources, setWebcamVidSources] = useState<RecordingSourceVideoUrls>({});
	const [roomScanVidSource, setRoomScanVidSource] = useState<{ [key: string]: string }>({});

	// Standardized video options
	const fetchVideoData = async (sessionUuid: string) => {
		setAreVideosFetched(false);
		// Events

		const allLoggedEvents = await getLoggedEvents(sessionUuid, courseId);
		const startDate = findStartDate(allLoggedEvents);
		setStartDate(startDate);
		const videoEvents = convertLoggingEventsToVideoEvents({ loggingEvents: allLoggedEvents, startDate });
		if (!videoEvents) {
			// TODO: handle something going wrong
			console.error("No video events");
			return;
		}
		setVideoEvents(videoEvents);

		// List of video sources and there status (available or processing)
		const [screencapturePromise, webcamPromise, roomScanPromise] = await Promise.allSettled([
			getVideoUrls(sessionUuid, "screencapture"),
			getVideoUrls(sessionUuid, "webcam"),
			getRoomScanVideoUrl(sessionUuid),
		]);

		if (screencapturePromise.status === "fulfilled") {
			screencapturePromise.value ? setScreenVidSources(screencapturePromise.value) : setScreenVidSources({});
		}

		if (webcamPromise.status === "fulfilled") {
			webcamPromise.value ? setWebcamVidSources(webcamPromise.value) : setWebcamVidSources({});
		}

		if (roomScanPromise.status === "fulfilled" && roomScanPromise.value) {
			setRoomScanVidSource(roomScanPromise.value);
		} else {
			setRoomScanVidSource({});
		}

		setAreVideosFetched(true);
	};

	const updateVideoEvents = useCallback(
		async (sessionUuid: string) => {
			const allLoggedEvents = await getLoggedEvents(sessionUuid, courseId);
			const videoEvents = convertLoggingEventsToVideoEvents({ loggingEvents: allLoggedEvents, startDate });
			if (!videoEvents) {
				// TODO: handle something going wrong
				console.error("No video events");
				return;
			}
			setVideoEvents(videoEvents);
		},
		[courseId, startDate],
	);

	const startProctoringEvent = useMemo(() => {
		return videoEvents.find((e) => {
			return e && e.type === "session" && e.name === "start proctoring" && e.details?.success;
		});
	}, [videoEvents]);

	const showAudioThumbnail = useMemo(() => {
		return (
			startProctoringEvent?.details?.options?.videoRecordingMode === "off" &&
			startProctoringEvent?.details?.options?.audioRecordingMode != "off"
		);
	}, [
		startProctoringEvent?.details?.options?.audioRecordingMode,
		startProctoringEvent?.details?.options?.videoRecordingMode,
	]);

	const shouldHideCamVid = useMemo(() => {
		return (
			startProctoringEvent?.details?.options?.videoRecordingMode === "off" &&
			startProctoringEvent?.details?.options?.audioRecordingMode != "off" &&
			!!startProctoringEvent?.details?.options?.recordScreen
		);
	}, [
		startProctoringEvent?.details?.options?.audioRecordingMode,
		startProctoringEvent?.details?.options?.videoRecordingMode,
		startProctoringEvent?.details?.options?.recordScreen,
	]);

	const proctoringOptions = useMemo((): Partial<ProctoringAssessmentConfigurationV1> => {
		if (!startProctoringEvent?.details?.options) {
			return {};
		}
		return startProctoringEvent.details.options as ProctoringAssessmentConfigurationV1;
	}, [startProctoringEvent?.details?.options]);

	const showVideoUnavailable = useMemo(() => {
		const screenAvail = screenVidSources ? areSourcesAvailable(screenVidSources) : false;
		const webcamAvail = webcamVidSources ? areSourcesAvailable(webcamVidSources) : false;
		return !screenAvail && !webcamAvail;
	}, [screenVidSources, webcamVidSources]);

	const firstVideoEvent = useMemo(() => {
		return videoEvents[0];
	}, [videoEvents]);

	const filteredLiveNotes = useMemo(() => {
		const videoEventTimes = videoEvents.map((x) => new Date(x.logged_time).getTime());
		const startTime = Math.min(...videoEventTimes);
		const endTime = Math.max(...videoEventTimes);
		return selectedLiveNotes?.filter(
			(x) =>
				videoEvents.length > 0 &&
				x.createdAt >= new Date(startTime) &&
				x.createdAt <= new Date(endTime) &&
				(x.edits?.length ?? 0) > 0,
		);
	}, [selectedLiveNotes, videoEvents]);

	const visibleLiveNotes = useMemo((): LiveProctoringSessionNote[] => {
		return (
			filteredLiveNotes?.map((x) => ({
				id: x.id,
				time: (x.createdAt.getTime() - new Date(firstVideoEvent?.logged_time).getTime()) / 1000,
				content: x.edits?.[x.edits.length - 1]?.content,
				date: x.createdAt,
			})) ?? []
		);
	}, [filteredLiveNotes, firstVideoEvent]);

	const noteEvents = useMemo((): VideoEvent[] => {
		return (
			filteredLiveNotes?.map((x) => {
				const time = (x.createdAt.getTime() - new Date(firstVideoEvent?.logged_time).getTime()) / 1000;

				return {
					key: x.id,
					time: time,
					name: "create",
					type: "live_session_note",
					details: {
						editorName: x.edits[0].editorName ?? null,
						noteContent: x.edits[x.edits.length - 1].content,
					},
					flag_level: 0,
					resolved: false,
					logged_time: x.createdAt.toISOString(),
				};
			}) ?? []
		);
	}, [filteredLiveNotes, firstVideoEvent]);

	const allEvents = useMemo(() => {
		const sortedEvents = [...videoEvents, ...noteEvents].sort((a, b) => a.time - b.time);
		return filterVideoEvents(sortedEvents);
	}, [videoEvents, noteEvents]);

	const webcamDuration = useMemo(() => {
		return ((allEvents[allEvents.length - 1]?.time ?? 0) - (allEvents[0]?.time ?? 0)) * 1000;
	}, [allEvents]);

	const eventsWithDurationMetadata = useMemo(() => {
		return addDurationMetaDataEvents(allEvents);
	}, [allEvents]);

	const firstEventDate = useMemo(() => {
		return new Date(allEvents[0]?.logged_time);
	}, [allEvents]);

	const isLoading = useMemo(() => {
		return !(eventsWithDurationMetadata?.length && eventsWithDurationMetadata.length > 0);
	}, [eventsWithDurationMetadata]);

	// For now, download only source 0
	const shouldShowDownload = useMemo(() => {
		return (
			areVideosFetched &&
			((webcamVidSources[0] &&
				Object.keys(webcamVidSources[0]).length > 0 &&
				!Object.values(webcamVidSources[0]).some((source) => source === "processing")) ||
				(screenVidSources[0] &&
					Object.keys(screenVidSources[0]).length > 0 &&
					!Object.values(screenVidSources[0]).some((source) => source === "processing")))
		);
	}, [areVideosFetched, screenVidSources, webcamVidSources]);

	// Find the "start quiz" event (or first "start recording" if "start quiz" doesn't exist),
	// and move the video to that event and
	// make the offset of the video move to that point to cut out the black screen.
	// TODO: should move this into useVideoControls. roomScan timeOffset is always 0, but webcam is this value -> this was originally in TimeStampLinks
	const timeOffset = useMemo(() => {
		let timeOff = -1;
		for (const event of eventsWithDurationMetadata) {
			if (event.name === "start quiz") {
				timeOff = event.time * 1000;
				break;
			} else if (event.name === "start recording") {
				if (timeOff === -1) {
					timeOff = event.time * 1000;
				}
			}
		}
		timeOff = Math.max(0, timeOff);
		return timeOff;
	}, [eventsWithDurationMetadata]);

	const flagCount = useMemo((): { severe?: number; major?: number; minor?: number } => {
		if (!eventsWithDurationMetadata || eventsWithDurationMetadata.length === 0) {
			return { severe: 0, major: 0, minor: 0 };
		}

		let severeCount;
		let majorCount;
		let minorCount;

		for (const ev of eventsWithDurationMetadata) {
			if (!isValidEvent(ev)) {
				continue;
			}
			if (ev.flag_level === 3) {
				severeCount = (severeCount ?? 0) + 1;
			} else if (ev.flag_level === 2) {
				majorCount = (majorCount ?? 0) + 1;
			} else if (ev.flag_level === 1) {
				minorCount = (minorCount ?? 0) + 1;
			}
		}
		severeCount = severeCount ?? 0;
		majorCount = majorCount ?? 0;
		minorCount = minorCount ?? 0;
		return { severe: severeCount, major: majorCount, minor: minorCount };
	}, [eventsWithDurationMetadata]);

	const hasTypedSignature = useMemo(() => videoEvents.some((ev) => ev.name === "typed signature"), [videoEvents]);

	const sessionRecordingTimestamps = useTimestamps(eventsWithDurationMetadata);

	const objectDetectionCapability = useMemo(
		() =>
			Boolean(
				proctoringOptions && proctoringOptions.objectDetectionMode && proctoringOptions.objectDetectionMode !== "off",
			),
		[proctoringOptions],
	);

	const faceDetectionCapability = useMemo(
		() =>
			Boolean(proctoringOptions && proctoringOptions.faceTrackingMode && proctoringOptions.faceTrackingMode !== "off"),
		[proctoringOptions],
	);

	const showMachineLearningOverlay = useMemo(
		() => objectDetectionCapability || faceDetectionCapability,
		[objectDetectionCapability, faceDetectionCapability],
	);

	return {
		action: {
			fetchVideoData,
			updateVideoEvents,
		},
		video: {
			areVideosFetched,
			videoEvents,
			shouldHideCamVid,
			webcamVidSources,
			screenVidSources,
			showVideoUnavailable,
			showAudioThumbnail,
			roomScanVidSource,
			shouldShowDownload,
			timeOffset,
			initialWebCamDuration: webcamDuration,
			showMachineLearningOverlay,
		},
		events: {
			all: eventsWithDurationMetadata,
			flagCount,
			hasTypedSignature,
			firstEventDate,
			sessionRecordingTimestamps,
			startDate,
		},
		state: {
			isLoading,
			proctoringOptions,
			visibleLiveNotes,
		},
	};
}
